/**
 * Centralized query key factory for React Query.
 *
 * This ensures consistent cache keys across all hooks and enables
 * cross-hook cache invalidation (e.g., invalidate all org data on org change).
 *
 * Pattern: Each entity has:
 * - `all`: Base key for invalidating everything for this entity
 * - `lists()`: Key for list queries
 * - `list(filters)`: Key for filtered list queries
 * - `detail(id)`: Key for single item queries
 */

export const queryKeys = {
  // Organization-scoped entities
  org: {
    all: (orgId: string) => ["org", orgId] as const,
    detail: (orgId: string) => ["org", orgId, "detail"] as const,
    members: (orgId: string) => ["org", orgId, "members"] as const,
    memberActivity: (orgId: string) =>
      ["org", orgId, "members", "activity"] as const,
    threads: (orgId: string) => ["org", orgId, "threads"] as const,
    usageSummary: (orgId: string, from: string, to: string) =>
      ["org", orgId, "usage", "summary", from, to] as const,
    usageTimeseries: (orgId: string, from: string, to: string) =>
      ["org", orgId, "usage", "timeseries", from, to] as const,
    usageByUser: (orgId: string, from: string, to: string) =>
      ["org", orgId, "usage", "by-user", from, to] as const,
  },

  // Threads
  threads: {
    all: ["threads"] as const,
    lists: () => [...queryKeys.threads.all, "list"] as const,
    list: (filters: { userId?: string; orgId?: string; projectId?: string }) =>
      [...queryKeys.threads.lists(), filters] as const,
    detail: (threadId: string) => ["threads", "detail", threadId] as const,
  },

  // Messages
  messages: {
    all: ["messages"] as const,
    list: (threadId: string) => ["messages", "list", threadId] as const,
    detail: (messageId: string) => ["messages", "detail", messageId] as const,
  },

  // Projects
  projects: {
    all: ["projects"] as const,
    lists: () => [...queryKeys.projects.all, "list"] as const,
    list: (filters: { userId?: string; orgId?: string }) =>
      [...queryKeys.projects.lists(), filters] as const,
    detail: (projectId: string) => ["projects", "detail", projectId] as const,
    files: (projectId: string) =>
      ["projects", "detail", projectId, "files"] as const,
    threads: (projectId: string) =>
      ["projects", "detail", projectId, "threads"] as const,
  },

  // Brains
  brains: {
    all: ["brains"] as const,
    lists: () => [...queryKeys.brains.all, "list"] as const,
    list: (filters: { orgId?: string }) =>
      [...queryKeys.brains.lists(), filters] as const,
    detail: (brainId: string) => ["brains", "detail", brainId] as const,
  },

  // Skills
  skills: {
    all: ["skills"] as const,
    lists: () => [...queryKeys.skills.all, "list"] as const,
    list: (filters: { orgId?: string }) =>
      [...queryKeys.skills.lists(), filters] as const,
    detail: (skillId: string) => ["skills", "detail", skillId] as const,
  },

  // Playbooks
  playbooks: {
    all: ["playbooks"] as const,
    lists: () => [...queryKeys.playbooks.all, "list"] as const,
    list: (filters: { orgId?: string; type?: string; status?: string }) =>
      [...queryKeys.playbooks.lists(), filters] as const,
    detail: (playbookId: string) =>
      ["playbooks", "detail", playbookId] as const,
    executions: (playbookId: string) =>
      ["playbooks", "detail", playbookId, "executions"] as const,
    execution: (executionId: string) =>
      ["playbooks", "execution", executionId] as const,
    executionFiles: (executionId: string, role?: string) =>
      ["playbooks", "execution", executionId, "files", role ?? "all"] as const,
    reports: (playbookId: string) =>
      ["playbooks", "detail", playbookId, "reports"] as const,
  },

  workRecords: {
    all: ["workRecords"] as const,
    lists: () => [...queryKeys.workRecords.all, "list"] as const,
    list: (filters: {
      orgId: string;
      workflowKey?: string;
      statuses?: string[];
      phases?: string[];
      ageHours?: number;
      lockedOnly?: boolean;
    }) => [...queryKeys.workRecords.lists(), filters] as const,
    detail: (recordId: string) =>
      ["workRecords", "detail", recordId] as const,
  },

  workArtifacts: {
    all: ["workArtifacts"] as const,
    lists: () => [...queryKeys.workArtifacts.all, "list"] as const,
    list: (filters: {
      orgId: string;
      workRecordId: string;
      roles?: string[];
      currentOnly?: boolean;
      includeDeleted?: boolean;
      visibility?: string[];
    }) => [...queryKeys.workArtifacts.lists(), filters] as const,
  },

  phaseRunners: {
    all: ["phaseRunners"] as const,
    health: (filters: { orgId: string; workflowKey?: string }) =>
      [...queryKeys.phaseRunners.all, "health", filters] as const,
    ticks: (runnerId: string) =>
      ["phaseRunners", "ticks", runnerId] as const,
  },

  orchestrators: {
    all: ["orchestrators"] as const,
    lists: () => [...queryKeys.orchestrators.all, "list"] as const,
    list: (filters: { orgId?: string }) =>
      [...queryKeys.orchestrators.lists(), filters] as const,
    detail: (orchestratorId: string) =>
      ["orchestrators", "detail", orchestratorId] as const,
    runs: (orchestratorId: string) =>
      ["orchestrators", "detail", orchestratorId, "runs"] as const,
    run: (runId: string) => ["orchestrators", "run", runId] as const,
    runItems: (runId: string) =>
      ["orchestrators", "run", runId, "items"] as const,
    preview: (orgId: string) => ["orchestrators", "preview", orgId] as const,
    collections: (orgId: string) =>
      ["orchestrators", "collections", orgId] as const,
    collection: (collectionId: string) =>
      ["orchestrators", "collection", collectionId] as const,
    collectionItems: (collectionId: string) =>
      ["orchestrators", "collection", collectionId, "items"] as const,
  },

  // Dashboards (ontology)
  dashboards: {
    all: ["dashboards"] as const,
    lists: () => [...queryKeys.dashboards.all, "list"] as const,
    list: (filters: { orgId: string }) =>
      [...queryKeys.dashboards.lists(), filters] as const,
    detail: (dashboardId: string) =>
      ["dashboards", "detail", dashboardId] as const,
    context: (dashboardId: string) =>
      ["dashboards", "detail", dashboardId, "context"] as const,
  },

  // Views (lakehouse)
  views: {
    all: ["views"] as const,
    lists: () => [...queryKeys.views.all, "list"] as const,
    list: (filters: {
      orgId: string;
      search?: string;
      chartType?: string;
      folder?: string;
    }) => [...queryKeys.views.lists(), filters] as const,
    count: (orgId: string) => ["views", "count", orgId] as const,
    detail: (viewId: string) => ["views", "detail", viewId] as const,
    execution: (viewId: string, filterHash?: string) =>
      ["views", "execution", viewId, filterHash] as const,
  },

  // Metrics (ontology)
  metrics: {
    all: ["metrics"] as const,
    lists: () => [...queryKeys.metrics.all, "list"] as const,
    list: (filters: { orgId: string }) =>
      [...queryKeys.metrics.lists(), filters] as const,
    detail: (metricId: string) => ["metrics", "detail", metricId] as const,
  },

  // Datasources (ontology)
  // - `list`         → raw `DataSourceModel[]` from the backend (used by
  //                    OrgProvider prefetch and the legacy datasets pages
  //                    that read them as DataSourceModel).
  // - `connections`  → transformed `DatabaseConnection[]` shape used by
  //                    the Sources surface (database_type, host, port…).
  // These MUST live in distinct sub-keys because the two shapes are
  // not interchangeable: a tile reading `db.database_type` against the
  // raw shape gets `undefined` and renders as a gray cloud.
  datasources: {
    all: ["datasources"] as const,
    lists: () => [...queryKeys.datasources.all, "list"] as const,
    list: (filters: { orgId: string }) =>
      [...queryKeys.datasources.lists(), filters] as const,
    connections: (filters: { orgId: string }) =>
      [...queryKeys.datasources.all, "connections", filters] as const,
    detail: (datasourceId: string) =>
      ["datasources", "detail", datasourceId] as const,
  },

  // Namespaces (lakehouse)
  namespaces: {
    all: ["namespaces"] as const,
    lists: () => [...queryKeys.namespaces.all, "list"] as const,
    list: (filters: { orgId: string }) =>
      [...queryKeys.namespaces.lists(), filters] as const,
    overview: (namespaceId: string) =>
      ["namespaces", "detail", namespaceId, "overview"] as const,
  },

  // Tables (lakehouse)
  tables: {
    all: ["tables"] as const,
    lists: () => [...queryKeys.tables.all, "list"] as const,
    list: (filters: { orgId: string; namespace?: string }) =>
      [...queryKeys.tables.lists(), filters] as const,
    detail: (tableId: string) => ["tables", "detail", tableId] as const,
    data: (tableId: string, params?: { limit?: number; offset?: number }) =>
      ["tables", "detail", tableId, "data", params] as const,
    schemas: (orgId: string) => ["tables", "schemas", orgId] as const,
  },

  // Data sources (unified iceberg + foreign + composite)
  dataSources: {
    all: ["dataSources"] as const,
    lists: () => [...queryKeys.dataSources.all, "list"] as const,
    list: (filters: {
      orgId: string;
      sourceType?: string;
      search?: string;
      limit?: number;
      offset?: number;
      fetchAll?: boolean;
    }) => [...queryKeys.dataSources.lists(), filters] as const,
    detail: (dataSourceId: string) =>
      ["dataSources", "detail", dataSourceId] as const,
  },

  // Foreign connections
  foreignConnections: {
    all: ["foreignConnections"] as const,
    lists: () => [...queryKeys.foreignConnections.all, "list"] as const,
    list: (filters: { orgId: string; federationEnabledOnly?: boolean }) =>
      [...queryKeys.foreignConnections.lists(), filters] as const,
    detail: (connectionId: string) =>
      ["foreignConnections", "detail", connectionId] as const,
    schemas: (connectionId: string) =>
      ["foreignConnections", "detail", connectionId, "schemas"] as const,
  },

  // API connections
  apiConnections: {
    all: ["apiConnections"] as const,
    lists: () => [...queryKeys.apiConnections.all, "list"] as const,
    list: (filters: { orgId: string }) =>
      [...queryKeys.apiConnections.lists(), filters] as const,
  },

  // Semantic objects (ontology)
  semanticObjects: {
    all: ["semanticObjects"] as const,
    lists: () => [...queryKeys.semanticObjects.all, "list"] as const,
    list: (filters: { orgId: string }) =>
      [...queryKeys.semanticObjects.lists(), filters] as const,
    detail: (objectId: string) =>
      ["semanticObjects", "detail", objectId] as const,
    dataSample: (objectId: string, page: number, pageSize: number) =>
      [
        "semanticObjects",
        "detail",
        objectId,
        "dataSample",
        { page, pageSize },
      ] as const,
  },

  // Semantic relationships (ontology)
  semanticRelationships: {
    all: ["semanticRelationships"] as const,
    lists: () => [...queryKeys.semanticRelationships.all, "list"] as const,
    list: (filters: { orgId: string }) =>
      [...queryKeys.semanticRelationships.lists(), filters] as const,
  },

  // Metric calculations (ontology)
  metricCalculations: {
    all: ["metricCalculations"] as const,
    list: (filters: { orgId: string }) =>
      ["metricCalculations", "list", filters] as const,
  },

  // Available filters (ontology)
  filters: {
    all: ["filters"] as const,
    available: (type: string, id: string) =>
      ["filters", "available", type, id] as const,
  },

  // Lakehouse stats
  lakehouseStats: {
    all: ["lakehouseStats"] as const,
    summary: (orgId: string) => ["lakehouseStats", "summary", orgId] as const,
  },

  // Monitor overview (playbooks)
  monitor: {
    all: ["monitor"] as const,
    overview: (orgId: string) => ["monitor", "overview", orgId] as const,
  },

  // Knowledge Bases
  knowledgeBases: {
    all: ["knowledgeBases"] as const,
    lists: () => [...queryKeys.knowledgeBases.all, "list"] as const,
    list: (filters: { orgId: string }) =>
      [...queryKeys.knowledgeBases.lists(), filters] as const,
    detail: (kbId: string) => ["knowledgeBases", "detail", kbId] as const,
  },

  // KB Folders
  kbFolders: {
    all: ["kbFolders"] as const,
    lists: () => [...queryKeys.kbFolders.all, "list"] as const,
    list: (kbId: string) => [...queryKeys.kbFolders.lists(), kbId] as const,
  },

  // KB Files
  kbFiles: {
    all: ["kbFiles"] as const,
    lists: () => [...queryKeys.kbFiles.all, "list"] as const,
    list: (filters: { kbId: string; folderId?: string | null }) =>
      [...queryKeys.kbFiles.lists(), filters] as const,
    detail: (fileId: string) => ["kbFiles", "detail", fileId] as const,
  },

  // Organizations (auth)
  organizations: {
    all: ["organizations"] as const,
    list: (userId: string) => ["organizations", "list", userId] as const,
  },

  // Pipedream connections
  pipedreamConnections: {
    all: ["pipedreamConnections"] as const,
    lists: () => [...queryKeys.pipedreamConnections.all, "list"] as const,
    list: (filters: { orgId?: string }) =>
      [...queryKeys.pipedreamConnections.lists(), filters] as const,
    apps: (filters: { orgId?: string }) =>
      [...queryKeys.pipedreamConnections.all, "apps", filters] as const,
    browse: (filters: {
      orgId?: string;
      connectionId?: string;
      query?: string;
      folderId?: string | null;
      siteId?: string | null;
      accountId?: string | null;
      driveId?: string | null;
    }) => [...queryKeys.pipedreamConnections.all, "browse", filters] as const,
  },

  // User contexts
  userContexts: {
    all: ["userContexts"] as const,
    list: (userId: string) => ["userContexts", "list", userId] as const,
  },
};
